// API utility with automatic auth token handling and session expiry detection

// Session expiry callback - will be set by the App component
let onSessionExpired = null;

export const setSessionExpiredCallback = (callback) => {
  onSessionExpired = callback;
};

export const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem('authToken');

  const defaultOptions = {
    credentials: 'include', // Keep for session fallback
    headers: {
      ...options.headers,
    },
  };

  if (!(options.body instanceof FormData) && !options.headers?.['Content-Type']) {
    defaultOptions.headers['Content-Type'] = 'application/json';
  }
  

  // Add auth token if available
  if (token) {
    defaultOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  // Send the admin's selected company so the backend can restore it if the
  // server session lost it (otherwise saves fail with "No company selected").
  try {
    const storedUserInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
    if (storedUserInfo?.selectedCompany) {
      defaultOptions.headers['X-Selected-Company'] = encodeURIComponent(storedUserInfo.selectedCompany);
    }
  } catch (e) {
    // Ignore malformed stored user info
  }

  try {
    const response = await fetch(url, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    });

    // Check for session expiry or unauthorized access
    if (response.status === 401) {
      // Check if this is a session expiry by trying to get auth status
      const authCheckUrl = `${process.env.REACT_APP_BACKEND_API_URL}/auth/check`;

      // Don't create infinite loop by checking auth status from auth check
      if (!url.includes('/auth/check')) {
        try {
          const authResponse = await fetch(authCheckUrl, {
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (authResponse.status === 401) {
            // Session has expired, trigger logout
            if (onSessionExpired) {
              onSessionExpired();
            }
          }
        } catch (authError) {
          console.error('Error checking auth status:', authError);
          // If we can't check auth status, assume session expired
          if (onSessionExpired) {
            onSessionExpired();
          }
        }
      }
    }

    return response;
  } catch (error) {
    // Network error or other fetch error
    console.error('API request error:', error);
    throw error;
  }
};

// Wrapper for common HTTP methods
export const api = {
  get: (url, options = {}) => apiRequest(url, { ...options, method: 'GET' }),
  post: (url, data, options = {}) => {
    // Handle FormData differently
    if (data instanceof FormData) {
      return apiRequest(url, { 
        ...options, 
        method: 'POST', 
        body: data 
      });
    }
    return apiRequest(url, { 
      ...options, 
      method: 'POST', 
      body: JSON.stringify(data) 
    });
  },
  put: (url, data, options = {}) => {
    // Handle FormData differently
    if (data instanceof FormData) {
      return apiRequest(url, { 
        ...options, 
        method: 'PUT', 
        body: data 
      });
    }
    return apiRequest(url, { 
      ...options, 
      method: 'PUT', 
      body: JSON.stringify(data) 
    });
  },
  delete: (url, options = {}) => apiRequest(url, { ...options, method: 'DELETE' }),
};